import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Skeleton } from '@/components/ui/skeleton';
import { copyToClipboard } from '@/lib/format';
import type { AlertItem } from '@/types/api';
import type { TFunction } from 'i18next';

interface NeedsAttentionProps {
  alerts: AlertItem[];
  total: number;
  isLoading: boolean;
  isError: boolean;
  onRetry: () => void;
}

function daysAgo(days: number, t: TFunction): string {
  if (days <= 0) return t('common.daysAgoToday');
  return t('common.daysAgo', { count: days });
}

function alertMeta(alert: AlertItem, t: TFunction): string {
  if (alert.alertType === 'grace_period') {
    return `${t('overview.attention.renewalOverdue')} · ${t('overview.attention.expiredPrefix')} ${daysAgo(alert.daysSinceExpiry, t)}`;
  }
  if (alert.alertType === 'expired') {
    return `${t('overview.attention.subscriptionEnded')} · ${daysAgo(alert.daysSinceExpiry, t)}`;
  }
  return `${t('overview.attention.revoked')} · ${daysAgo(alert.daysSinceExpiry, t)}`;
}

function alertStatus(
  alert: AlertItem,
  t: TFunction
): { word: string; className: string; dotClass: string } {
  if (alert.alertType === 'grace_period') {
    return {
      word: t('common.status.grace'),
      className: 'text-status-pending',
      dotClass: 'bg-status-pending',
    };
  }
  if (alert.alertType === 'expired') {
    return {
      word: t('common.status.expired'),
      className: 'text-status-expired',
      dotClass: 'bg-status-expired',
    };
  }
  return {
    word: t('common.status.revoked'),
    className: 'text-status-revoked',
    dotClass: 'bg-status-revoked',
  };
}

export function NeedsAttention({
  alerts,
  total,
  isLoading,
  isError,
  onRetry,
}: NeedsAttentionProps) {
  const { t } = useTranslation();

  if (isError) {
    return (
      <div>
        <h2 className="text-xl font-semibold tracking-tight text-foreground">
          {t('overview.attention.title')}
        </h2>
        <p className="mt-4 text-sm text-destructive">{t('overview.attention.error')}</p>
        <button onClick={onRetry} className="mt-2 text-sm text-primary underline">
          {t('common.retry')}
        </button>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-5 flex items-baseline justify-between gap-4">
        <h2 className="text-xl font-semibold tracking-tight text-foreground">
          {t('overview.attention.title')}
          {!isLoading && total > 0 && (
            <span className="ml-2.5 inline-block min-w-[22px] rounded-full bg-primary/15 px-2 py-[3px] text-center align-[2px] text-xs font-semibold text-primary">
              {total}
            </span>
          )}
        </h2>
      </div>

      {isLoading ? (
        <div className="space-y-3">
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
          <Skeleton className="h-14 w-full" />
        </div>
      ) : alerts.length === 0 ? (
        <p className="text-sm text-muted-foreground">{t('overview.attention.empty')}</p>
      ) : (
        <>
          {alerts.slice(0, 5).map((alert, i) => {
            const status = alertStatus(alert, t);
            return (
              <Link
                key={alert.id}
                to={`/licenses/${alert.id}`}
                className={`grid grid-cols-[1fr_auto] items-center gap-4 border-b px-1 py-4 transition-colors first:border-t hover:bg-foreground/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring ${
                  i === 0 ? 'rounded-t-md' : ''
                }`}
              >
                <div className="min-w-0">
                  <code
                    className="block cursor-copy truncate font-mono text-sm font-medium text-foreground"
                    onClick={(e) => {
                      e.preventDefault();
                      e.stopPropagation();
                      void copyToClipboard(alert.serial);
                    }}
                    title={t('common.clickToCopy')}
                  >
                    {alert.serial}
                  </code>
                  <div className="mt-1 text-[13px] text-faint">{alertMeta(alert, t)}</div>
                </div>
                <span
                  className={`inline-flex items-center gap-2 whitespace-nowrap text-[13.5px] font-medium ${status.className}`}
                >
                  <span className={`h-2 w-2 rounded-full ${status.dotClass}`} aria-hidden="true" />
                  {status.word}
                </span>
              </Link>
            );
          })}
          {total > 5 && (
            <Link
              to="/licenses?status=expired"
              className="mt-4 inline-block text-sm font-medium text-primary underline-offset-4 hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              {t('overview.attention.viewAll', { total })}
            </Link>
          )}
        </>
      )}
    </div>
  );
}
